import { useContext, useState } from "react";

import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import SectionHeader from "../../components/SectionHeader";
import TableLoader from "../../components/TableLoader";

import { PartnerContext } from "../../contexts/PartnerContext";
import { AuthContext } from "../../contexts/AuthContext";

import usePartnerStaffAssignedProjects from "../../hooks/usePartnerStaffAssignedProjects";
import { formatDateToSydneyTimeZone } from "../../utils/dateUtils";

const ProjectItems = ({ data }) => {
  const userCompanyName = (project, userId) => {
    if (project.engineeringCompany.staff.includes(userId)) return project.engineeringCompany.name;
    if (project.designCompany.staff.includes(userId)) return project.designCompany.name;
  }

  return (
    <div className='border-t border-b border-ase-blue max-w-full'>
      {data.assignedTo && data.assignedTo.length > 0 && (
        <p className="p-4 text-sm border-b border-[#0000001f]">
          <span className='font-bold'>Assigned to:</span> {data.assignedTo.map(u => `${u.fullName} (${userCompanyName(data, u._id)})`).join(', ')}
        </p>
      )}

      {data.items.length > 0 ? (
        <div>
          {data.items.map((item, index) => (
            <div>
              <div className='flex items-center justify-between gap-2 text-sm border-b border-[#0000001f]'>
                <div className='flex items-center gap-4'>
                  <div className='px-4 font-bold'>
                    <p>#{index + 1}</p>
                  </div>

                  <div className='flex flex-col gap-2 px-4 py-4'>
                    <p>{item.name}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='flex items-center justify-center h-[72px]'>
          <p className='text-sm'>There are no records to display</p>
        </div>
      )}
    </div>
  )
}

const PartnerStaffProjects = () => {
  const { partnerCompany } = useContext(PartnerContext);
  const { user } = useContext(AuthContext);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);

  const [selectedStatus, setSelectedStatus] = useState(null);

  const { data: partnerCompanyData, isLoading } = usePartnerStaffAssignedProjects({
    shouldFetch: !!partnerCompany,
    staffId: user?._id
  });

  const projects = partnerCompanyData?.projects;
  const count = partnerCompanyData?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      sortField: 'name'
    },
    {
      name: 'Quote #',
      selector: row => row.aseId,
      sortable: true,
      sortField: 'aseId'
    },
    {
      name: 'Client',
      selector: row => row.client,
      sortable: true,
      sortField: 'client'
    },
    {
      name: 'Status',
      selector: row => row.status !== 'Completed' ? 'In progress' : 'Completed',
      sortable: true,
      sortField: 'status',
      conditionalCellStyles: [
        {
          when: row => row.status !== 'Completed',
          classNames: ['text-ase-blue']
        },
        {
          when: row => row.status === 'Completed',
          classNames: ['text-green-600']
        }
      ]
    },
    {
      name: 'Start date',
      selector: row => row.startDate,
      format: row => formatDateToSydneyTimeZone(row.startDate),
      sortable: true,
      sortField: 'startDate'
    },
    {
      name: 'End date',
      selector: row => row.endDate,
      format: row => formatDateToSydneyTimeZone(row.endDate),
      sortable: true,
      sortField: 'endDate'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Projects | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader title='Projects' />

        <DataTable
          columns={columns}
          data={projects}
          striped
          highlightOnHover
          pointerOnHover
          expandableRows
          expandableRowsComponent={ProjectItems}
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={count}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>
    </>
  );
};

export default PartnerStaffProjects;
